import type { CSSProperties } from "react";
import Skeleton from "react-loading-skeleton";
import { css } from "../../../../styled-system/css";
import { Flex, styled, VStack } from "../../../../styled-system/jsx";
import { HoverInfo } from "../basic/Tooltip";
import TotalSalesVendorWarning from "./TotalSalesVendorWarning";
import { formatMetricValue, type FormatOptions } from "../../../utils/formatNumber";
import { useLocalStorage } from "@reactuses/core";
import { useAppSelector } from "../../../redux/hooks";
import {
  metricNameSelector,
  metricToolTipSelector,
  type MetricConfig,
} from "../../../modules/merchandising/utils/metrics";
import { MetricChangeRow } from "./MetricChangeRow";
import { useTranslation } from "react-i18next";

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
interface Props<T = any> {
  metric: MetricConfig<T>;
  data?: T;
  selected?: boolean;
  loading?: boolean;
  showTarget?: boolean;
}

const SelectedIndicator = styled("div", {
  base: {
    borderColor: "var(--color)",
    borderRadius: "light",
    width: "6px",
    backgroundColor: "var(--color)",
    transition: "all 0.30s ease-in-out",
  },
  variants: {
    isSelected: {
      true: { boxShadow: `0 0 14px var(--color)` },
      false: { backgroundColor: `color-mix(in srgb, var(--color) 20%, transparent)` },
    },
  },
});

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
const SummaryMetric = <T = any,>({ metric, data, selected, loading = false, showTarget = false }: Props<T>) => {
  const [numberFormat] = useLocalStorage("numberFormat", "compact");
  const { marketPlace } = useAppSelector((state) => state.marketPlace);
  const { t } = useTranslation(["merchandising", "metrics"]);

  // Extract values from metric config
  const name =
    metric.name == null ? t(metricNameSelector(metric.id), { defaultValue: metric.id })
    : typeof metric.name === "string" ? metric.name
    : t(metric.name);
  const value = metric.accessor(data);
  const prev = metric.prev_accessor ? metric.prev_accessor(data) : null;
  const deltaValue = metric.delta_accessor ? metric.delta_accessor(data) : null;
  const percentValue = metric.percent_accessor ? metric.percent_accessor(data) : null;
  const toolTip =
    metric.toolTip == null ? t(metricToolTipSelector(metric.id), { defaultValue: "" })
    : typeof metric.toolTip === "string" ? metric.toolTip
    : t(metric.toolTip);
  const legendColor = metric.color;
  const compareHighlight = metric.compareHighlight ?? "positiveIsGood";
  const hasComparison = Boolean(metric.delta_accessor || metric.percent_accessor);

  // Build format options from metric config
  const decimalPlaces = metric.decimalPlaces ?? (metric.format === "percent" ? 1 : 2);

  const formatOptions: FormatOptions = {
    format: metric.format as "number" | "percent" | "currency",
    decimalPlaces,
    compact: metric.compact,
    marketPlace,
  };

  const forceCompact =
    formatOptions.format === "percent" ? false : (formatOptions.compact ?? numberFormat !== "precise");

  const adjustedFormatOptions: FormatOptions =
    formatOptions.format === "percent" ? formatOptions : { ...formatOptions, compact: forceCompact };

  const displayValue = formatMetricValue(value, adjustedFormatOptions);
  const displayPrevValue = formatMetricValue(prev, adjustedFormatOptions);

  // Prepare delta format for MetricChangeRow
  const deltaFormat: FormatOptions = {
    format: metric.format === "percent" ? "number" : (metric.format as "number" | "percent" | "currency"),
    decimalPlaces: metric.decimalPlaces ?? 1,
    compact: true,
    addPlus: true,
    marketPlace,
  };

  // Adjust delta value for percent metrics (like MetricCell does)
  const adjustedDeltaValue = metric.format === "percent" && deltaValue ? deltaValue * 100 : deltaValue;

  return (
    <Flex gap="small" justifyContent="flex-end">
      <VStack gap="small" justifyContent="space-between" alignItems={"end"}>
        <h6
          className={css({
            display: "flex",
            gap: "xsmall",
            justifyContent: "end",
            alignItems: "start",
            height: "25px",
            margin: 0,
            fontSize: "medium",
            fontWeight: selected ? 800 : 600,
            lineHeight: "1",
            textAlign: "end",
            transition: "all 0.30s ease-in-out",
          })}
        >
          <TotalSalesVendorWarning metricId={metric.id} />
          {name}
          {toolTip && <HoverInfo iconCss={{ color: "grey", fontSize: "small" }}>{toolTip}</HoverInfo>}
        </h6>
        <VStack gap="xsmall" alignItems={"end"}>
          <h3
            className={css({
              margin: 0,
              fontSize: "xlarge",
              fontWeight: 600,
              lineHeight: "1",
            })}
          >
            {loading ?
              <Skeleton width={"50px"} />
            : displayValue}
          </h3>
          {showTarget && (prev != null || loading) && (
            <span
              className={css({
                color: "text.secondary",
                fontSize: "xsmall",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              })}
            >
              {loading ?
                <Skeleton width={"80px"} height={"12px"} />
              : t(($) => $.merchandising["COMPARE {value}"], { value: displayPrevValue })}
            </span>
          )}
          {hasComparison && (
            <MetricChangeRow
              delta={adjustedDeltaValue}
              percent={percentValue}
              highlight={compareHighlight}
              deltaFormat={deltaFormat}
              marketPlace={marketPlace}
              loading={loading}
            />
          )}
        </VStack>
      </VStack>
      {legendColor ?
        <SelectedIndicator
          isSelected={selected}
          style={
            {
              "--color": legendColor,
            } as CSSProperties
          }
        />
      : null}
    </Flex>
  );
};

export default SummaryMetric;
